// Base
import { BaseApi } from 'api/base.api';

// Interface
import {
  ICertificationWebpageGeneralProofApi,
  ICertificationWebpageGeneralProofApiRequest,
  ICertificationWebpageGeneralProofApiListResponse,
  ICertificationWebpageGeneralProofApiNullResponse,
  ICertificationWebpageGeneralProofApiGetListAttrs,
  ICertificationWebpageGeneralProofApiUploadDocumentAttrs,
  ICertificationWebpageGeneralProofApiUpdateAttrs,
  ICertificationWebpageGeneralProofApiDeleteDocumentAttrs,
  ICertificationWEbpageGeneralProofApiUploadDocumentResponse,
  ICertificationWebpageGeneralProofApiDownloadDocumentAttrs,
  ICertificationWebpageGeneralProofApiDownloadDocumentResponse,
} from 'interfaces/webpage/certification/registration/precondition';

// Constant
import { ApiConstantHc, ApiConstantHcBase } from 'constant';

// Utils
import { axios_renderHcUrl } from 'utils';

export class CertificationWebpageGeneralProofApi
  extends BaseApi<ICertificationWebpageGeneralProofApi>
  implements ICertificationWebpageGeneralProofApiRequest
{
  readonly url = ApiConstantHc.CERTIFICATION_REGISTRATION_PRECONDITION_GENERAL_PROOF_URL;
  private certificationRegistrationGeneralProofUrl = axios_renderHcUrl(this.url, ApiConstantHcBase.ASSESSMENT);

  /**
   * @description Get general proof list
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {ICertificationWebpageGeneralProofApiGetListAttrs} payload
   *
   * @return {Promise<ICertificationWebpageGeneralProofApiListResponse>} Promise<ICertificationWebpageGeneralProofApiListResponse>
   */
  async certificationWebpageGeneralProof_getList(
    payload: ICertificationWebpageGeneralProofApiGetListAttrs,
  ): Promise<ICertificationWebpageGeneralProofApiListResponse> {
    return this.POST(
      `${this.certificationRegistrationGeneralProofUrl}/list/${payload.params.assessmentId}`,
      {},
      { ...payload.requestConfig },
    );
  }

  /**
   * @description Update general proof
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {ICertificationWebpageGeneralProofApiUpdateAttrs} payload
   *
   * @return {Promise<ICertificationWebpageGeneralProofApiNullResponse>} Promise<ICertificationWebpageGeneralProofApiNullResponse>
   */
  async certificationWebpageGeneralProof_update(
    payload: ICertificationWebpageGeneralProofApiUpdateAttrs,
  ): Promise<ICertificationWebpageGeneralProofApiNullResponse> {
    return this.POST(`${this.certificationRegistrationGeneralProofUrl}/update/${payload.params.assessmentId}`, payload.body, {
      ...payload.requestConfig,
    });
  }

  /**
   * @description Upload general proof document
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {ICertificationWebpageGeneralProofApiUploadDocumentAttrs} payload
   *
   * @return {Promise<ICertificationWEbpageGeneralProofApiUploadDocumentResponse>} Promise<ICertificationWEbpageGeneralProofApiUploadDocumentResponse>
   */
  async certificationWebpageGeneralProof_uploadDocument(
    payload: ICertificationWebpageGeneralProofApiUploadDocumentAttrs,
  ): Promise<ICertificationWEbpageGeneralProofApiUploadDocumentResponse> {
    const formData = new FormData();
    formData.append('document', payload.body.document);
    formData.append('answerCode', payload.body.answerCode);

    if (payload.body.transactionFileId) formData.append('transactionFileId', payload.body.transactionFileId.toString());

    return this.POST(`${this.certificationRegistrationGeneralProofUrl}/uploadDocument/${payload.params.assessmentId}`, formData, {
      ...payload.requestConfig,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  /**
   * @description Delete general proof document
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {ICertificationWebpageGeneralProofApiDeleteDocumentAttrs} payload
   *
   * @return {Promise<ICertificationWebpageGeneralProofApiNullResponse>} Promise<ICertificationWebpageGeneralProofApiNullResponse>
   */
  async certificationWebpageGeneralProof_deleteDocument(
    payload: ICertificationWebpageGeneralProofApiDeleteDocumentAttrs,
  ): Promise<ICertificationWebpageGeneralProofApiNullResponse> {
    return this.POST(
      `${this.certificationRegistrationGeneralProofUrl}/deleteDocument/${payload.params.userFileTransactionId}`,
      {},
      { ...payload.requestConfig },
    );
  }

  /**
   * @description Download document of general proof
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {ICertificationWebpageGeneralProofApiDownloadDocumentAttrs} payload
   *
   * @return {Promise<ICertificationWebpageGeneralProofApiDownloadDocumentResponse>} Promise<ICertificationWebpageGeneralProofApiDownloadDocumentResponse>
   */
  async certificationWebpageGeneralProof_downloadDocument(
    payload: ICertificationWebpageGeneralProofApiDownloadDocumentAttrs,
  ): Promise<ICertificationWebpageGeneralProofApiDownloadDocumentResponse> {
    return this.POST(
      `${this.certificationRegistrationGeneralProofUrl}/download/${payload.params.blobId}`,
      {},
      { ...payload.requestConfig, responseType: 'blob' },
    );
  }
}
